import { useEffect, useRef } from 'react';
import { Modal, View, Text, Pressable, Animated } from 'react-native';
import { wordleResultOverlayStyles as styles } from './WordleResultOverlay.styles';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { flagEmojiToCountryCode } from '../../../src/quiz/helpers/flagEmoji.helper';
import { countryCodeFromName } from '../../../src/quiz/helpers/countryFlag.helper';
import type { WordleReveal, WordleStreak } from '../../../src/quiz/helpers/gamesApi';

interface WordleResultOverlayProps {
  visible: boolean;
  won: boolean;
  reveal: WordleReveal | null;
  shareGrid: string;
  guessesUsed: number;
  maxGuesses: number;
  streak: WordleStreak;
  coinsAwarded: number;
  onShare: () => void;
  onClose: () => void;
}

/** Build the 🟩/🟨/⬛ block (without the header line) from the share text the server returned. */
function gridFromShare(share: string): string {
  const lines = share.split('\n');
  return lines.slice(2).join('\n');
}

export function WordleResultOverlay({
  visible,
  won,
  reveal,
  shareGrid,
  guessesUsed,
  maxGuesses,
  streak,
  coinsAwarded,
  onShare,
  onClose,
}: WordleResultOverlayProps) {
  const scale = useRef(new Animated.Value(0.85)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(scale, { toValue: 1, tension: 60, friction: 7, useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: true }),
      ]).start();
    } else {
      scale.setValue(0.85);
      opacity.setValue(0);
    }
  }, [visible, scale, opacity]);

  const sub = won
    ? `Solved in ${guessesUsed}/${maxGuesses} · ${streak.current} 🔥 streak`
    : `Tomorrow's another one · ${streak.current} 🔥 streak`;

  // Curated answers ship a flag emoji; ELITE answers ship a country name. Render from whichever exists.
  const flagCode = reveal
    ? reveal.flag
      ? flagEmojiToCountryCode(reveal.flag)
      : countryCodeFromName(reveal.nationality)
    : null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Animated.View style={[styles.card, { opacity, transform: [{ scale }] }]}>
          <Text style={styles.emoji}>{won ? '🎉' : '😅'}</Text>
          <Text style={styles.title}>
            {won ? (guessesUsed === 1 ? 'First try! 🔥' : 'You called it') : 'Out of guesses'}
          </Text>

          {won && coinsAwarded > 0 ? (
            <View style={styles.coinChip}>
              <Text style={styles.coinChipText}>{`+${coinsAwarded} coins`}</Text>
            </View>
          ) : null}

          {reveal ? (
            <View style={styles.revealCard}>
              {flagCode != null ? (
                <CountryFlag
                  countryCode={flagCode}
                  size="compact"
                  accessibilityLabel={reveal.fullName}
                />
              ) : (
                <Text style={styles.revealFlag}>{reveal.flag ?? ''}</Text>
              )}
              <View>
                <Text style={styles.revealName}>{reveal.fullName}</Text>
                <Text style={styles.revealClub}>{reveal.club}</Text>
              </View>
            </View>
          ) : null}

          <Text style={styles.sub}>{sub}</Text>
          <Text style={styles.grid}>{gridFromShare(shareGrid)}</Text>

          <Pressable onPress={onShare} style={({ pressed }) => [styles.shareButton, pressed && styles.pressed]}>
            <Text style={styles.shareButtonText}>Share result</Text>
          </Pressable>
          <Pressable onPress={onClose} style={({ pressed }) => [styles.closeButton, pressed && styles.pressed]}>
            <Text style={styles.closeButtonText}>Done</Text>
          </Pressable>
        </Animated.View>
      </View>
    </Modal>
  );
}
